/**
 * socialOAuth — schlanke, wiederverwendbare API-Schicht für die Social-Connect-Endpoints.
 *
 * Bündelt die bisher in SocialConnectPage verstreuten axios-Aufrufe an EINER Stelle.
 * Vorteil: ein Vertrag, einheitliche Fehlerbehandlung, leicht testbar/mockbar, und die
 * Komponenten bleiben frei von URL-/Endpoint-Wissen.
 *
 * Endpoints (Backend):
 *   GET    /social-connect/connect-url/{provider}  → { auth_url } | { needs_credentials } | { setup_needed }
 *   GET    /social-oauth/redirect-uris             → kanonische Callback-URIs je Provider
 *   GET    /social-oauth/config-check              → Go-Live-Bereitschaft je Provider
 *   POST   /social-oauth/bluesky/connect           → Bluesky per App-Passwort
 */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Kanonische Redirect-/Callback-URIs je Provider (secret-frei, für die Portal-Einrichtung). */
export async function fetchRedirectUris() {
  const res = await axios.get(`${API}/social-oauth/redirect-uris`);
  return res.data; // { public_base_url, redirect_uris: { provider: { redirect_uri, source } } }
}

/**
 * Preflight-Bereitschaft je Provider (secret-frei): ready/configured/redirect_uri/missing_env.
 * Nutzt /social-oauth/config-check. Bei Fehler → leeres Ergebnis (UI zeigt dann neutral).
 */
export async function fetchConfigCheck() {
  try {
    const res = await axios.get(`${API}/social-oauth/config-check`);
    return res.data; // { public_base_url, ready_count, channels: { provider: { ready, configured, ... } } }
  } catch {
    return { channels: {}, ready_count: 0 };
  }
}

/**
 * Connect für einen Provider starten — gibt eine klare, navigationsfreie Aktion zurück.
 * Die aufrufende Komponente entscheidet, wie sie reagiert (redirect / Hinweis / Fehler).
 *
 * @returns {Promise<{action:'redirect', url:string}
 *                  | {action:'setup_needed', message:string}
 *                  | {action:'error', message:string}>}
 */
export async function startConnect(provider) {
  try {
    const res = await axios.get(`${API}/social-connect/connect-url/${provider}`);
    const data = res.data || {};
    if (data.auth_url) return { action: "redirect", url: data.auth_url, demoMode: !!data.demo_mode };
    if (data.needs_credentials) return { action: "credentials", provider, kind: data.credential_kind, message: data.message };
    if (data.setup_needed) return { action: "setup_needed", message: data.message || "Dieser Kanal wird vorbereitet." };
    return { action: "error", message: "Keine Anmelde-URL erhalten — bitte später erneut versuchen." };
  } catch (err) {
    return { action: "error", message: err?.response?.data?.detail || "Verbindung fehlgeschlagen." };
  }
}

/**
 * Optionale KI-Setup-Hilfe ("In einfachen Worten"). Schickt nur Metadaten/Fakten —
 * NIE Secrets. Bei Fehler → { help_text: null } (Aufrufer zeigt freundlichen Fallback).
 */
export async function fetchSetupHelp(payload) {
  try {
    const res = await axios.post(`${API}/social-oauth/setup-help`, payload, { timeout: 12000 });
    return res.data; // { enabled, source, help_text }
  } catch {
    return { enabled: true, source: "error", help_text: null };
  }
}

/** Bluesky per Handle + App-Passwort verbinden (AT Protocol, kein OAuth-Redirect). */
export async function connectBluesky(handle, appPassword) {
  try {
    const res = await axios.post(`${API}/social-oauth/bluesky/connect`, {
      handle: handle,
      app_password: appPassword,
    });
    return { ok: true, ...res.data };
  } catch (err) {
    return { ok: false, message: err?.response?.data?.detail || "Anmeldung fehlgeschlagen." };
  }
}

// ─── Facebook-Seitenauswahl (Rebind) ─────────────────────────────────────────
// Der Facebook-User verwaltet oft mehrere Seiten. Statt stur die erste zu binden,
// listet /facebook/pages alle verfügbaren Seiten auf (aus dem gespeicherten
// User-Token) und /facebook/select-page bindet die vom Nutzer GEWÄHLTE Seite.
// Kein Klartext-Token verlässt jemals das Backend — hier fließen nur Metadaten.

/**
 * Verfügbare Facebook-Seiten des verbundenen Kontos laden.
 * @returns {Promise<{ok:true, pages:Array, current_page_id:string|null,
 *                     user_token_valid:boolean}
 *                  | {ok:false, message:string}>}
 * Bei ungültigem/abgelaufenem User-Token liefert das Backend user_token_valid=false
 * + leere Liste (Signal: „neu verbinden"), KEIN Fehler.
 */
export async function fetchFacebookPages() {
  try {
    const res = await axios.get(`${API}/social-oauth/facebook/pages`);
    const data = res.data || {};
    return {
      ok: true,
      pages: Array.isArray(data.pages) ? data.pages : [],
      current_page_id: data.current_page_id ?? null,
      user_token_valid: data.user_token_valid !== false,
    };
  } catch (err) {
    return {
      ok: false,
      message: err?.response?.data?.detail || "Seiten konnten nicht geladen werden.",
    };
  }
}

/**
 * Die gewählte Facebook-Seite verbindlich binden (Rebind).
 * Das Backend prüft, dass page_id zum Konto gehört, snapshottet die bisherige
 * Bindung (für Rollback) und übernimmt Seiten-Token + Instagram-Verknüpfung.
 * @returns {Promise<{ok:true, page_id, page_name, instagram:object|null,
 *                     rollback_available:boolean}
 *                  | {ok:false, message:string}>}
 */
export async function selectFacebookPage(pageId) {
  try {
    const res = await axios.post(`${API}/social-oauth/facebook/select-page`, {
      page_id: pageId,
    });
    return { ok: true, ...res.data };
  } catch (err) {
    return {
      ok: false,
      message: err?.response?.data?.detail || "Seite konnte nicht gebunden werden.",
    };
  }
}

/**
 * Letzte Seitenbindung rückgängig machen (stellt previous_binding wieder her).
 * @returns {Promise<{ok:true, ...}|{ok:false, message:string}>}
 */
export async function rollbackFacebookPage() {
  try {
    const res = await axios.post(`${API}/social-oauth/facebook/rollback-page`);
    return { ok: true, ...res.data };
  } catch (err) {
    return {
      ok: false,
      message: err?.response?.data?.detail || "Rückgängig machen fehlgeschlagen.",
    };
  }
}
